import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getSourceById,
  getSourceEtlConfig,
  startAggregationJob,
  startExtractionJob,
} from "../api/quontxApi";
import Pagination from "../components/Pagination";

const ITEMS_PER_PAGE = 9; // A 3x3 grid looks nice

const SourceEtlConfigs = () => {
  const { sourceUuid } = useParams();
  const navigate = useNavigate();

  const [loadingData, setLoadingData] = useState(true);
  const [extractingSource, setExtractingSource] = useState(false);
  const [aggregatingSource, setAggregatingSource] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [extractionJobLoading, setExtractionJobLoading] = useState({});

  const [source, setSource] = useState();
  const [etlCfgs, setEtlCfgs] = useState([]);

  // Add state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const handleBackClick = () => {
    navigate("/sources");
  };

  const handlePageChange = (page) => {
    loadEtlConfigs(page);
  };

  const loadSourceData = useCallback(async () => {
    const source = await getSourceById(sourceUuid);
    setSource(source.data);
  }, [sourceUuid]);

  const loadEtlConfigs = useCallback(
    async (page) => {
      try {
        const response = await getSourceEtlConfig(
          page,
          ITEMS_PER_PAGE,
          sourceUuid
        );
        // Parse the API response structure (adjust based on your actual API response)
        setEtlCfgs(response.data.data || response.data || []);
        setTotalItems(response.data.total_count || response.total || 0);
        setCurrentPage(page);
      } catch (err) {
        setErrorMsg("Failed to fetch ETL configurations.");
        console.error(err);
      }
    },
    [sourceUuid]
  );

  const startSourceAggregation = async () => {
    try {
      setAggregatingSource(true);
      await startAggregationJob(sourceUuid);
      alert(`Aggregation job started successfully for ${source.name}`);
    } catch (error) {
      console.error("Error starting job:", error);
      alert("Failed to start aggregation job. Please try again.");
    } finally {
      setAggregatingSource(false);
    }
  };

  const startSourceExtraction = async () => {
    try {
      setExtractingSource(true);
      await startExtractionJob(source.uuid, null);
      alert(`Extraction job started successfully for ${source.name}`);
    } catch (error) {
      console.error("Error starting job:", error);
      alert("Failed to start extraction job. Please try again.");
    } finally {
      setExtractingSource(false);
    }
  };

  const startEtlExtraction = async (etlConfig) => {
    try {
      setExtractionJobLoading((prev) => ({ ...prev, [etlConfig.uuid]: true }));
      await startExtractionJob(null, etlConfig.uuid);
      alert(`Extraction job started successfully for ${etlConfig.uuid}`);
    } catch (error) {
      console.error("Error starting job:", error);
      alert("Failed to start extraction job. Please try again.");
    } finally {
      setExtractionJobLoading((prev) => ({ ...prev, [etlConfig.uuid]: false }));
    }
  };

  useEffect(() => {
    (async () => {
      setLoadingData(true);
      await loadSourceData();
      await loadEtlConfigs(currentPage);
      setLoadingData(false);
    })();
  }, [currentPage, loadEtlConfigs, loadSourceData]);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  if (loadingData)
    return <div className="text-center p-8">Loading ETL configurations...</div>;
  if (errorMsg)
    return <div className="text-center p-8 text-red-400">{errorMsg}</div>;

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      {/* Back Button */}
      <button
        onClick={handleBackClick}
        className="mb-4 py-2 bg-qtx-darker text-white rounded hover:bg-qtx-dark transition-colors"
      >
        Back to sources
      </button>
      <h1 className="text-3xl font-bold text-white mb-2">Source</h1>
      {source && (
        <div>
          <p className="text-qtx-light-gray mb-6">Source: {source.name}</p>
          <p className="text-qtx-gray mt-2">ID: {source.uuid}</p>
          <p className="text-qtx-light-gray mb-6">
            Type:{" "}
            <span className="font-mono bg-qtx-dark px-2 py-1 rounded">
              {source.url_root}
            </span>
          </p>
          <div className="flex items-center gap-4">
            <button
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => startSourceExtraction()}
            >
              {extractingSource
                ? "Starting Extraction Job..."
                : "Start Extraction Job"}
            </button>
            {source.aggregator && (
              <button
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => startSourceAggregation()}
              >
                {aggregatingSource
                  ? "Starting Aggregation..."
                  : "Start Aggregation Job"}
              </button>
            )}
          </div>
        </div>
      )}

      {etlCfgs.length === 0 && !loadingData ? (
        <div className="text-center p-8">
          <div className="bg-qtx-darker p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold text-white mb-2">
              No ETL Configurations
            </h2>
            <p className="text-qtx-gray">
              This source doesn't have any ETL configurations yet.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-white mb-2">
            ETL Configurations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {etlCfgs.map((config) => (
              <div
                key={config.id || config.uuid}
                className="bg-qtx-darker p-6 rounded-lg shadow-lg"
              >
                <h2 className="text-xl font-semibold text-white">
                  {config.name}
                </h2>
                <p className="text-qtx-gray mt-2">ID: {config.uuid}</p>

                {config.extractor && (
                  <p className="text-qtx-light-gray mt-1">
                    Extractor: {config.extractor}
                  </p>
                )}

                {config.last_extracted_at && (
                  <p className="text-qtx-light-gray mt-1">
                    Last extracted:{" "}
                    {new Date(config.last_extracted_at).toLocaleDateString()}
                  </p>
                )}

                {/* Configuration Details */}
                {config.config && (
                  <div className="mt-3 p-3 bg-qtx-dark rounded">
                    <h4 className="text-sm font-medium text-qtx-light-gray mb-2">
                      Configuration:
                    </h4>
                    <div className="text-xs text-qtx-gray space-y-1">
                      {config.config.table_name && (
                        <div>Table: {config.config.table_name}</div>
                      )}
                      {config.config.schedule && (
                        <div>Schedule: {config.config.schedule}</div>
                      )}
                      {config.config.batch_size && (
                        <div>Batch Size: {config.config.batch_size}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Start Extraction Job Button */}
                <div className="mt-4">
                  <button
                    onClick={() => startEtlExtraction(config)}
                    disabled={extractionJobLoading[config.uuid]}
                    className="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {extractionJobLoading[config.uuid]
                      ? "Starting Extraction Job..."
                      : "Start Extraction Job"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default SourceEtlConfigs;
